import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import games from "virtual:obean-games";
import { GameHost } from "../host/host.js";
import { LocalDriver } from "../host/local-driver.js";
import { GameUnavailable, loadGame, reloadOnce } from "../host/loader.js";
import type { GameRef } from "../host/loader.js";
import { track } from "../host/telemetry.js";
import { PlayerInput, TouchControls, keyboardHint } from "../input/index.js";
import { FpsOverlay } from "./FpsOverlay.js";

/** Sandbox-only debug handle (the e2e tests read the simulation through it). */
type SandboxWindow = Window & { obeanSandbox?: { state: () => unknown } };

/**
 * Sandbox: plays a built-in game locally in this browser (no server), for trying the game on a
 * device. `/play/local?game=beanguard` (and `&version=` to ask for a specific bundle, `&mission=` for a
 * mission other than the first, `&players=` for more than the game's minimum).
 */
export function LocalPlay() {
  const stage = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState("Loading…");
  /** A tutorial mission keeps the controls hint up; otherwise it fades after a few seconds. */
  const [tutorial, setTutorial] = useState(false);
  const input = useMemo(() => new PlayerInput(), []);
  const params = new URLSearchParams(location.search);
  const touchUi = params.has("touch") || matchMedia("(pointer: coarse)").matches;
  /** The game's buttons (ADR 0017), for the touch controls and the keyboard hint. */
  const game = games.find((g) => g.id === (params.get("game") ?? "beanguard"));
  const buttons = game?.buttons ?? [];
  /** How touch screens steer it (ADR 0023). */
  const mode = game?.touch ?? "stick";

  useEffect(() => {
    const id = params.get("game") ?? "beanguard";
    const ref: GameRef = {
      id,
      version: params.get("version") ?? games.find((g) => g.id === id)?.version ?? "",
    };
    let host: GameHost | null = null;
    let cancelled = false; // set by the cleanup below, possibly while loading
    const isCancelled = () => cancelled;

    const start = async () => {
      const loadStartedAt = performance.now();
      const game = await loadGame(ref);
      const container = stage.current;
      if (isCancelled() || !container) return;
      host = await GameHost.load({ container, game, ref, track, loadStartedAt });
      if (isCancelled()) {
        host.exit(); // the page was left while the game was loading
        return;
      }
      const missionId = params.get("mission") ?? game.manifest.missions[0]?.id ?? "default";
      setTutorial(game.manifest.missions.find((m) => m.id === missionId)?.tutorial === true);
      // `&role=` picks this device's loadout (e.g. a tank role); other seats keep the game's default.
      const role = params.get("role");
      // `&players=` sets how many play (bots fill the others); a pair, the game's minimum, if not.
      const { min, max } = game.manifest.players;
      const wanted = Number(params.get("players") ?? min);
      const count = Number.isInteger(wanted) ? Math.min(max, Math.max(min, wanted)) : min;
      const players = Array.from({ length: count }, (_, slot) =>
        slot === 0 && role
          ? { slot, personId: `local-${slot}`, loadout: role }
          : { slot, personId: `local-${slot}` },
      );
      const local = host;
      // Slot 0 is this device's player (keyboard and touch, sampled once per step). Other seats get
      // the game's bot if it has one, else they stand still.
      const bots = players.map((p) => (p.slot === 0 ? null : (game.createBot?.(p.slot) ?? null)));
      const driver: LocalDriver<unknown, unknown> = new LocalDriver(
        game.rules,
        { seed: Math.floor(Math.random() * 2 ** 31), missionId, players },
        (slot) => {
          if (slot === 0) return local.input(input.sample());
          const bot = bots[slot];
          return bot ? bot(driver.state) : game.rules.idleInput();
        },
        () => performance.now(),
      );
      (window as SandboxWindow).obeanSandbox = { state: () => driver.state };
      host.start(
        { matchId: `local-${Date.now().toString(36)}`, missionId, mySlot: 0, players },
        driver,
      );
      setStatus("");
    };

    start().catch((err: unknown) => {
      if (err instanceof GameUnavailable && reloadOnce(err.ref)) return;
      setStatus(err instanceof Error ? err.message : String(err));
    });
    return () => {
      cancelled = true;
      host?.exit();
      input.dispose();
    };
  }, [input]);

  return (
    <section
      class={touchUi && (mode !== "tap" || buttons.length > 0) ? `play ${mode}` : "play"}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
    >
      <div ref={stage} class="stage" />
      {touchUi ? (
        <TouchControls touch={input.touch} buttons={buttons} mode={mode} />
      ) : mode === "tap" && buttons.length === 0 ? null : (
        // A tap game without buttons shows its own keys (ADR 0027): "Move" would be wrong.
        <p class={tutorial ? "hint stay" : "hint"}>{keyboardHint(buttons)}</p>
      )}
      {params.has("debug") && <FpsOverlay />}
      {status && <p role="status">{status}</p>}
    </section>
  );
}
